import React, { useContext, useState } from 'react';
import { GoClock, GoCalendar, GoSync } from "react-icons/go";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MobileTimePicker, MobileDatePicker } from "@mui/x-date-pickers";
import { TextField, InputAdornment } from '@mui/material';
import { RiSparkling2Fill, RiGroupLine } from 'react-icons/ri';
import { Language } from '../../../App';
import { adminBaseURL } from '../../../constants/API';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useCustomToast } from '../../common/CustomToast';
import moment from 'moment';
import { useCheckSubscriptionPermission } from '../../../hooks/useCheckSubscriptionPermission';
import './formTheme.css';

const ClinicalSessionTab = ({ onHide, setProgress, handleApiCall }) => {
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();
  const checkPermission = useCheckSubscriptionPermission('upload_clinical_session');

  const SplashData = JSON.parse(localStorage.getItem("SplashData") || "{}");
  const subcategories = SplashData?.home?.subcategories || [];

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    startDate: '',
    startTime: '',
    timeLimit: '',
    visibility: 'One Time',
    caption: '',
  });

  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [tempDate, setTempDate] = useState(null);
  const [tempTime, setTempTime] = useState(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleDateChange = (newDate) => {
    setTempDate(newDate);
    setStartDate(newDate);
    setFormData((prevData) => ({
      ...prevData,
      startDate: moment(newDate).format("YYYY-MM-DD"),
    }));
  };

  const handleTimeChange = (newTime) => {
    setTempTime(newTime);
    setStartTime(newTime);
    setFormData((prevData) => ({
      ...prevData,
      startTime: moment(newTime).format("hh:mm A"),
    }));
  };

  const handleTimePickerOpen = () => {
    setTempTime(moment().add(5, 'minutes'));
    setStartTime(moment().add(5, 'minutes'));
    setFormData((prevData) => ({
      ...prevData,
      startTime: moment().add(5, 'minutes').format("hh:mm A"),
    }));
  };

  const handleTimeOpen = (type) => {
    if (type === "start_time") {
      showToast(lang?.select_start_date_first || "Select start date first!", "error");
      return false;
    }
    return true;
  };

  function isValidFutureDate(inputDate) {
    const userDate = new Date(inputDate);
    if (isNaN(userDate.getTime())) {
      showToast(lang?.error_note_number || "Invalid date", "error");
      return false;
    }
    const today = new Date();
    userDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (userDate < today) {
      showToast(lang?.date_should_greater_todays_date || "Date should be today or a future date", "error");
      return false;
    }
    return true;
  }

  const handleSubmit = async () => {
    const combinedDateTimeString = `${formData.startDate} ${formData.startTime}`;
    const combinedDateTime = moment(combinedDateTimeString, "YYYY-MM-DD hh:mm A");
    const start_timestamp = combinedDateTime.valueOf();
    const _start_timestamp = Math.floor(start_timestamp / 1000);

    if (formData.timeLimit > 120) {
      showToast(lang?.clinical_session_time_limit || "Session duration cannot exceed 120 minutes.", "error");
      return 0;
    }

    if (formData.timeLimit < 0) {
      showToast(lang?.enter_correct_time_limit || "Please enter a valid duration.", "error");
      return 0;
    }
    if (combinedDateTime < new Date()) {
      showToast(lang?.start_time_less_current_time || "Start time cannot be in the past.", "error");
      return 0;
    }

    isValidFutureDate(formData.startDate);
    if (formData.timeLimit < 15) {
      showToast(lang?.clinical_session_duration_greater_15_mins || "Session duration must be at least 15 minutes", "error");
      return 0;
    }

    const { data: userToken } = JSON.parse(localStorage.getItem("SignupData") || "{}");
    const data = {
      start_timestamp: _start_timestamp,
      type: "clinical-session",
      caption: formData.caption,
      schedule_date: formData.startDate,
      schedule_time: formData.startTime,
      category_id: "3",
      subcategory_id: formData.category,
      title: formData.title,
      visibility: formData.visibility,
      duration: formData.timeLimit,
    };

    try {
      const response = await axios.post(adminBaseURL + "user/post/create", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${userToken?.token}`,
        },
        onUploadProgress: (progressData) => {
          setProgress(Math.round((100 * progressData.loaded) / progressData.total));
        },
      });

      if (response?.data?.status) {
        setFormData({
          title: '',
          category: '',
          startDate: '',
          startTime: '',
          timeLimit: '',
          visibility: 'One Time',
          caption: '',
        });
        setStartDate(null);
        setStartTime(null);
        return response;
      } else {
        showToast(response?.data?.error, "error");
      }
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      category: '',
      startDate: '',
      startTime: '',
      timeLimit: '',
      visibility: 'One Time',
      caption: '',
    });
    setStartDate(null);
    setStartTime(null);
    onHide();
  };

  const futureTime = startDate
    ? moment(startDate).isSame(moment(), 'day')
      ? moment().add(5, 'minutes')
      : moment(startDate).startOf('day').add(5, 'minutes')
    : moment().add(5, 'minutes');

  const _handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkPermission()) return;
    onHide();
    await handleApiCall(() => handleSubmit(), { type: "clinical-session" });
  };

  return (
    <>
      <div className="pf-form-body">
        {/* AI Banner */}
        <div className="pf-ai-banner">
          <div className="pf-ai-banner-left">
            <RiGroupLine className="pf-ai-banner-sparkle" size={16} />
            <span>Interactive Case Discussion & Grand Rounds</span>
          </div>
          <span className="pf-ai-banner-tag">Multi-Specialist</span>
        </div>

        <form onSubmit={_handleSubmit}>
          {/* Title & Category Grid */}
          <div className="pf-grid-2">
            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.title || "Session Topic"}</span>
              </div>
              <input
                type="text"
                className="pf-input"
                id="title"
                placeholder={lang?.title || "e.g., Complex Retinal Detachment Case Reviews"}
                value={formData.title}
                onChange={handleChange}
              />
            </div>

            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.select_category || "Medical Subspecialty"}</span>
              </div>
              <select
                className="pf-select"
                id="category"
                value={formData.category}
                onChange={handleChange}
                style={{ color: formData.category === "" ? "#94A3B8" : "#0B132B" }}
              >
                <option value="" style={{ color: "#94A3B8" }}>
                  {lang?.select_category || "Select medical field"}
                </option>
                {subcategories.map((value) => (
                  <option key={value.id} value={value.id} style={{ color: "#0B132B" }}>
                    {value.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date & Time Schedule Card */}
          <div className="pf-card-section d-flex flex-column gap-2.5">
            <span className="fs-13 fw-600" style={{ color: "#0B132B" }}>Session Schedule</span>
            <div className="pf-grid-2">
              <div className="pf-field">
                <div className="pf-label">
                  <span className="pf-label-text">
                    <GoCalendar className="pf-label-icon" />
                    {lang?.start_date || "Date"}
                  </span>
                </div>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                    value={startDate}
                    defaultValue={tempDate}
                    minDate={moment().startOf('day')}
                    onChange={handleDateChange}
                    slotProps={{
                      dialog: { className: "pf-picker-dialog-session" },
                      popper: { className: "pf-picker-dialog-session" },
                      textField: {
                        placeholder: "MM/DD/YYYY",
                        className: "pf-mui-field",
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <GoCalendar style={{ cursor: 'pointer', color: '#3B82F6', fontSize: '16px' }} />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="pf-field">
                <div className="pf-label">
                  <span className="pf-label-text">
                    <GoClock className="pf-label-icon" />
                    {lang?.start_time || "Start Time"}
                  </span>
                </div>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  {startDate ? (
                    <MobileTimePicker
                      value={startTime}
                      minTime={futureTime}
                      onChange={handleTimeChange}
                      onOpen={handleTimePickerOpen}
                      slotProps={{
                        dialog: { className: "pf-picker-dialog-session" },
                        popper: { className: "pf-picker-dialog-session" },
                        textField: {
                          placeholder: "hh:mm aa",
                          className: "pf-mui-field",
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <GoClock style={{ cursor: 'pointer', color: '#3B82F6', fontSize: '16px' }} />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  ) : (
                    <TextField
                      placeholder="hh:mm aa"
                      className="pf-mui-field"
                      onClick={() => handleTimeOpen("start_time")}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <GoClock style={{ cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </LocalizationProvider>
              </div>
            </div>
          </div>

          {/* Duration & Commitment Grid */}
          <div className="pf-grid-2">
            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.time_limit || "Session Duration"}</span>
                <span className="pf-optional-badge">15 to 120 mins</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  className="pf-input"
                  id="timeLimit"
                  placeholder="e.g., 45"
                  value={formData.timeLimit || ""}
                  onChange={handleChange}
                />
                <span className="pf-chip-count" style={{ padding: "6px 12px", borderRadius: "10px", fontSize: "11.5px" }}>
                  Min
                </span>
              </div>
            </div>

            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.clinical_session_commitment || "Recurrence Pattern"}</span>
              </div>
              <div className="pf-segment-group">
                {['One Time', 'Daily', 'Weekly'].map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={`pf-segment-btn ${formData.visibility === item ? 'pf-segment-btn--active' : ''}`}
                    onClick={() => setFormData((prev) => ({ ...prev, visibility: item }))}
                  >
                    <GoSync size={12} />
                    <span>{item}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Description Textarea */}
          <div className="pf-field">
            <div className="pf-label">
              <span className="pf-label-text">{lang?.description || "Case Discussion Agenda & Objectives"}</span>
            </div>
            <textarea
              className="pf-textarea"
              id="caption"
              rows={3}
              placeholder={lang?.description || "Describe the clinical cases to be presented, imaging needed, and discussion points..."}
              value={formData.caption || ""}
              onChange={handleChange}
            />
          </div>

          {/* Bottom Actions */}
          <div className="pf-footer-row" style={{ justifyContent: "flex-end" }}>
            <div className="pf-footer-actions">
              <button
                type="button"
                className="pf-btn-cancel"
                onClick={handleCancel}
              >
                {lang?.cancel || "Cancel"}
              </button>
              <button
                disabled={
                  !formData.caption ||
                  !formData.startDate ||
                  !formData.startTime ||
                  !formData.category ||
                  !formData.title ||
                  !formData.visibility ||
                  !formData.timeLimit
                }
                type="submit"
                className="pf-btn-submit"
              >
                <span>{lang?.publish || "Schedule Clinical Session"}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ClinicalSessionTab;
